import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Card, Button, Input, Alert } from '../../../shared/components/ui';
import { guestMenuService } from '../../menu/services/guestMenuService';
import authService from '../services/authService';
import Logo from '../../../shared/components/Logo';
import { Link as RouterLink } from 'react-router-dom';

const RegisterForm = ({ onRegisterSuccess, fromPayment = false, stripeSessionId = '' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    // Si viene desde pago, usar código automático, sino vacío
    const [invitationCode, setInvitationCode] = useState(fromPayment ? 'STRIPE_PAYMENT' : '');
    const [acceptPolicies, setAcceptPolicies] = useState(false);

    // Validación de contraseña con requisitos de seguridad mínimos
    const validatePassword = (pwd) => {
        if (!pwd) {
            return t('auth.validation.passwordRequired') || 'La contraseña es obligatoria';
        }
        if (pwd.length < 8) {
            return t('auth.validation.passwordMinLength') || 'La contraseña debe tener al menos 8 caracteres';
        }
        if (!/[A-Z]/.test(pwd)) {
            return t('auth.validation.passwordUppercase') || 'La contraseña debe contener al menos una letra mayúscula';
        }
        if (!/[a-z]/.test(pwd)) {
            return t('auth.validation.passwordLowercase') || 'La contraseña debe contener al menos una letra minúscula';
        }
        if (!/[0-9]/.test(pwd)) {
            return t('auth.validation.passwordNumber') || 'La contraseña debe contener al menos un número';
        }
        return '';
    };

    // Manejar cambios en la contraseña con validación
    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        
        // Validar solo si el usuario ha empezado a escribir
        if (newPassword.length > 0) {
            const validationError = validatePassword(newPassword);
            setPasswordError(validationError);
        } else {
            setPasswordError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validar contraseña con requisitos de seguridad
        const validationError = validatePassword(password);
        if (validationError) {
            setError(validationError);
            setPasswordError(validationError);
            setLoading(false);
            return;
        }

        // Validar que las contraseñas coincidan
        if (password !== confirmPassword) {
            setError(t('auth.errors.passwordMismatch'));
            setLoading(false);
            return;
        }

        // Requerir código de invitación
        if (!invitationCode.trim()) {
            setError(t('auth.errors.invitationCodeRequired'));
            setLoading(false);
            return;
        }

        try {
            if (!acceptPolicies) {
                setError(t('auth.register.acceptPoliciesError'));
                setLoading(false);
                return;
            }
            const data = await guestMenuService.register(
                email,
                password,
                invitationCode.trim(),
                stripeSessionId || null
            );

            // Guardar toda la información del usuario en un solo item de localStorage
            // (igual que en el login)
            const userData = {
                email: data.user?.email || email,
                preferences: data.preferences || null,
                token: data.token || null
            };
            localStorage.setItem('user_data', JSON.stringify(userData));

            // Actualizar el token en el authService
            if (data.token) {
                authService.setToken(data.token);
            }

            // Ejecutar callback si existe
            if (onRegisterSuccess) {
                onRegisterSuccess(data);
            }

            // Redirigir a /plan después de registro exitoso
            navigate('/plan');
        } catch (error) {
            // Mensaje específico si el email no coincide con el pago
            if (error.message && error.message.includes('email_mismatch')) {
                setError('El email debe ser el mismo que usaste al pagar con Stripe');
            } else if (error.message && error.message.includes('payment_verification_failed')) {
                setError('No se pudo verificar tu pago. Por favor, contacta con soporte.');
            } else {
                setError(error.message || t('auth.errors.createError'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo y título */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-2xl">🍽️</span>
                    </div>
                    {fromPayment ? (
                        <>
                            <div className="text-4xl mb-4">🎉</div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                ¡Pago Completado!
                            </h1>
                            <p className="text-gray-600">
                                Completa tu registro con el mismo email que usaste en el pago
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                <Trans
                                    i18nKey="auth.register.title"
                                    components={{ logo: <Logo inline /> }}
                                />
                            </h1>
                            <p className="text-gray-600">{t('auth.register.subtitle')}</p>
                        </>
                    )}
                </div>

                {/* Formulario de registro */}
                <Card className="shadow-xl" shadow="lg">
                    <Card.Header>
                        <Card.Title className="text-2xl font-bold text-center text-primary-700">
                            {t('auth.register.cardTitle')}
                        </Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Solo mostrar campo de código de invitación si NO viene desde pago */}
                            {!fromPayment && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('auth.register.invitationCodeLabel')}
                                    </label>
                                    <Input
                                        type="text"
                                        value={invitationCode}
                                        onChange={(e) => setInvitationCode(e.target.value)}
                                        placeholder={t('auth.register.invitationCodePlaceholder')}
                                        required
                                        className="w-full"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('auth.register.emailLabel')}
                                    {fromPayment && (
                                        <span className="ml-2 text-xs text-blue-600 font-normal">
                                            ⚠️ Debe ser el mismo email del pago
                                        </span>
                                    )}
                                </label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={fromPayment ? "Email usado en el pago" : t('auth.register.emailPlaceholder')}
                                    required
                                    className="w-full"
                                />
                                {fromPayment && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        El email debe coincidir con el que usaste al pagar con Stripe
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('auth.register.passwordLabel')}
                                </label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    placeholder={t('auth.register.passwordPlaceholder')}
                                    required
                                    className={`w-full ${passwordError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                />
                                {passwordError && (
                                    <p className="text-red-600 text-sm mt-1">{passwordError}</p>
                                )}
                                <div className="mt-2 text-xs text-gray-500">
                                    <p className="font-medium mb-1">{t('auth.validation.passwordRequirements') || 'Requisitos de la contraseña:'}</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li className={password.length >= 8 ? 'text-green-600' : ''}>
                                            {t('auth.validation.passwordMinLengthHint') || 'Mínimo 8 caracteres'}
                                        </li>
                                        <li className={/[A-Z]/.test(password) ? 'text-green-600' : ''}>
                                            {t('auth.validation.passwordUppercaseHint') || 'Al menos una letra mayúscula'}
                                        </li>
                                        <li className={/[a-z]/.test(password) ? 'text-green-600' : ''}>
                                            {t('auth.validation.passwordLowercaseHint') || 'Al menos una letra minúscula'}
                                        </li>
                                        <li className={/[0-9]/.test(password) ? 'text-green-600' : ''}>
                                            {t('auth.validation.passwordNumberHint') || 'Al menos un número'}
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('auth.register.confirmPasswordLabel')}
                                </label>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder={t('auth.register.confirmPasswordPlaceholder')}
                                    required
                                    className="w-full"
                                />
                            </div>

                            <div className="pt-2">
                                <label className="flex items-start gap-3 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={acceptPolicies}
                                        onChange={(e) => setAcceptPolicies(e.target.checked)}
                                        className="mt-1"
                                        required
                                    />
                                    <span>
                                        <Trans
                                            i18nKey="auth.register.acceptPoliciesLabel"
                                            components={{
                                                linkPrivacy: <RouterLink className="text-primary-600 hover:underline" to="/privacy" />,
                                                linkCookies: <RouterLink className="text-primary-600 hover:underline" to="/cookies" />
                                            }}
                                        />
                                    </span>
                                </label>
                            </div>

                            {error && (
                                <Alert variant="error">
                                    <p>{error}</p>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                loading={loading}
                                disabled={loading || !!passwordError || !password || !email || !confirmPassword || !invitationCode || !acceptPolicies}
                                className="w-full"
                            >
                                {loading ? t('auth.register.loading') : t('auth.register.button')}
                            </Button>
                        </form>
                    </Card.Content>
                </Card>

                {/* Enlaces adicionales */}
                <div className="text-center mt-6 space-y-4">
                    <p className="text-gray-600">
                        {t('auth.register.hasAccount')}{' '}
                        <Link
                            to="/login"
                            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
                        >
                            {t('auth.register.loginLink')}
                        </Link>
                    </p>

                    <Link
                        to="/"
                        className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        {t('auth.register.backHome')}
                    </Link>
                </div>

            </div>
        </div>
    );
};

export default RegisterForm;
